import React, { useState, useEffect } from 'react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { 
  Users, DollarSign, Activity, TrendingUp, ShieldAlert, Coins, 
  UserMinus, CreditCard, PlusCircle, CheckCircle2, AlertTriangle, X,
  ArrowRight, RefreshCw, Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';
import constant from '../constants/constant';

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [kpis, setKpis] = useState({
    totalUsers: 0,
    onlineUsers: 0,
    totalSellers: 0,
    totalRecharge: 0,
    totalCoins: 0,
    profit: 0,
    liveActivity: []
  });

  // Modal states
  const [modalType, setModalType] = useState(null); // 'seller', 'block', 'recharge'
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState('');
  const [modalSuccess, setModalSuccess] = useState('');

  // Form states
  const [sellerForm, setSellerForm] = useState({ first_name: '', last_name: '', email: '', password: '', whatsapp_number: '', aadhar_card: '', country: 'India' });
  const [showPassword, setShowPassword] = useState(false);
  const [blockForm, setBlockForm] = useState({ user_id: '', ban: true, reason: '' });
  const [rechargeForm, setRechargeForm] = useState({ target_user_id: '', amount: '', coins: '', wrong_id: '' });

  // Users lookup for recharge
  const [usersList, setUsersList] = useState([]);
  const [userSearch, setUserSearch] = useState('');

  // Users lookup for block user
  const [blockUsersList, setBlockUsersList] = useState([]);
  const [blockUserSearch, setBlockUserSearch] = useState('');
  
  const [allUsersCache, setAllUsersCache] = useState([]);

  // Countries
  const [countriesList, setCountriesList] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);

  useEffect(() => {
    fetchKPIs();
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    setCountriesLoading(true);
    try {
      const hardcodedCountries = [
        "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", "Australia", "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Canada", "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nauru", "Nepal", "Netherlands", "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway", "Oman", "Pakistan", "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
      ];
      setCountriesList(hardcodedCountries);
    } catch (err) {
      console.error("Error fetching countries:", err);
      setCountriesList(["India", "Pakistan", "Bangladesh", "UAE", "USA", "Other"]);
    } finally {
      setCountriesLoading(false);
    }
  };

  const fetchKPIs = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${constant.API_BASE_URL}/admin/business/dashboard/kpis`, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setKpis(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching KPIs:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSeller = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');
    setModalError('');
    setModalSuccess('');
    
    // Client-side validations for specific fields to provide better error messages
    if (sellerForm.whatsapp_number && sellerForm.whatsapp_number.length !== 10) {
      setModalError("WhatsApp Number must be exactly 10 digits.");
      setModalLoading(false);
      return;
    }
    if (sellerForm.aadhar_card && sellerForm.aadhar_card.length !== 12) {
      setModalError("Aadhaar Number must be exactly 12 digits.");
      setModalLoading(false);
      return;
    }
    
    try {
      const response = await axios.post(`${constant.API_BASE_URL}/auth/create-user`, {
        ...sellerForm,
        user_type: 'SELLER'
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        // Now submit application to main.sellers using admin token
        const newUserId = response.data.id;
        await axios.post(`${constant.API_BASE_URL}/admin/business/sellers/admin-add`, { new_user_id: newUserId }, {
          headers: { Authorization: sessionStorage.getItem('adminToken') }
        });

        setModalSuccess("Seller registered and approved successfully! They can now log in at /seller/login with their password.");
        setSellerForm({ first_name: '', last_name: '', email: '', password: '', whatsapp_number: '', aadhar_card: '', country: 'India' });
        fetchKPIs();
      }
    } catch (err) {
      setModalError(err.response?.data?.message || err.response?.data?.error || err.response?.data?.errors || "Failed to register seller");
    } finally {
      setModalLoading(false);
    }
  };

  const handleBlockUser = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');
    setModalSuccess('');
    if (!blockForm.user_id) {
      setModalError('Please select a user from the dropdown search list.');
      setModalLoading(false);
      return;
    }
    try {
      const banRes = await axios.put(`${constant.API_BASE_URL}/admin/business/users/${blockForm.user_id}/ban`, {
        ban: blockForm.ban
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });

      if (banRes.data.success) {
        if (blockForm.ban && blockForm.reason) {
          await axios.post(`${constant.API_BASE_URL}/admin/business/users/${blockForm.user_id}/warning`, {
            reason: blockForm.reason
          }, {
            headers: { Authorization: sessionStorage.getItem('adminToken') }
          });
        }
        setModalSuccess(blockForm.ban ? "User blocked successfully!" : "User unblocked successfully!");
        setBlockForm({ user_id: '', ban: true, reason: '' });
      }
    } catch (err) {
      setModalError(err.response?.data?.message || "Failed to update user status");
    } finally {
      setModalLoading(false);
    }
  };

  const handleSearchUsers = async (val) => {
    setUserSearch(val);
    if (val.length < 2) return;
    try {
      let users = allUsersCache;
      if (users.length === 0) {
        const res = await axios.get(`${constant.API_BASE_URL}/auth/users`, {
          headers: { Authorization: sessionStorage.getItem('adminToken') }
        });
        users = res.data || [];
        setAllUsersCache(users);
      }
      
      const filtered = users.filter(u => 
        (u.first_name + ' ' + (u.last_name || '')).toLowerCase().includes(val.toLowerCase()) || 
        (u.email || '').toLowerCase().includes(val.toLowerCase()) ||
        (u.user_id || '').toLowerCase().includes(val.toLowerCase()) ||
        (u.id || '').toString().includes(val)
      );
      setUsersList(filtered.slice(0, 10));
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearchBlockUsers = async (val) => {
    setBlockUserSearch(val);
    if (val.length < 2) return;
    try {
      let users = allUsersCache;
      if (users.length === 0) {
        const res = await axios.get(`${constant.API_BASE_URL}/auth/users`, {
          headers: { Authorization: sessionStorage.getItem('adminToken') }
        });
        users = res.data || [];
        setAllUsersCache(users);
      }
      
      const filtered = users.filter(u => 
        (u.first_name + ' ' + (u.last_name || '')).toLowerCase().includes(val.toLowerCase()) || 
        (u.email || '').toLowerCase().includes(val.toLowerCase()) ||
        (u.user_id || '').toLowerCase().includes(val.toLowerCase()) ||
        (u.id || '').toString().includes(val)
      );
      setBlockUsersList(filtered.slice(0, 10));
    } catch (err) {
      console.error(err);
    }
  };

// Number formatting helpers
const formatNumberWithCommas = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const numStr = String(val).replace(/,/g, '').replace(/[^0-9]/g, '');
  if (!numStr) return '';
  return Number(numStr).toLocaleString('en-IN');
};

const getRawNumber = (val) => {
  if (!val) return 0;
  const numStr = String(val).replace(/,/g, '').replace(/[^0-9]/g, '');
  return Number(numStr) || 0;
};

const formatAmountInWords = (num) => {
  const n = Number(num);
  if (!n || n <= 0) return '';
  if (n >= 10000000) {
    const cr = (n / 10000000).toFixed(2).replace(/\.00$/, '');
    return `${cr} Cr Coins (${n.toLocaleString('en-IN')})`;
  }
  if (n >= 100000) {
    const lk = (n / 100000).toFixed(2).replace(/\.00$/, '');
    return `${lk} Lakh Coins (${n.toLocaleString('en-IN')})`;
  }
  if (n >= 1000) {
    const th = (n / 1000).toFixed(2).replace(/\.00$/, '');
    return `${th}k Coins (${n.toLocaleString('en-IN')})`;
  }
  return `${n.toLocaleString('en-IN')} Coins`;
};

  const handleManualRecharge = async (e) => {
    e.preventDefault();
    setModalLoading(true);
    setModalError('');
    setModalSuccess('');
    if (!rechargeForm.target_user_id) {
      setModalError('Please select a user from the dropdown search list.');
      setModalLoading(false);
      return;
    }
    const rawCoins = getRawNumber(rechargeForm.coins);
    const rawAmount = String(rechargeForm.amount).replace(/,/g, '').replace(/[^0-9.]/g, '');
    if (rawCoins <= 0) {
      setModalError('Please enter a valid coins amount.');
      setModalLoading(false);
      return;
    }
    try {
      const response = await axios.post(`${constant.API_BASE_URL}/admin/business/recharges/manual`, {
        target_user_id: rechargeForm.target_user_id,
        amount: parseFloat(rawAmount) || 0,
        coins: rawCoins,
        wrong_id: rechargeForm.wrong_id
      }, {
        headers: { Authorization: sessionStorage.getItem('adminToken') }
      });
      if (response.data.success) {
        setModalSuccess(response.data.message || "Manual recharge processed successfully!");
        setRechargeForm({ target_user_id: '', amount: '', coins: '', wrong_id: '' });
        fetchKPIs();
      }
    } catch (err) {
      setModalError(err.response?.data?.message || "Recharge failed.");
    } finally {
      setModalLoading(false);
    }
  };

  const statsData = [
    { title: "Total Users", value: kpis.totalUsers, change: "+4.1%", isPositive: true, icon: Users, color: "text-blue-600", bg: "bg-blue-50" },
    { title: "Online Users", value: kpis.onlineUsers, change: "Live", isPositive: true, icon: Activity, color: "text-green-600", bg: "bg-green-50" },
    { title: "Active Sellers", value: kpis.totalSellers, change: "+8.3%", isPositive: true, icon: Users, color: "text-purple-600", bg: "bg-purple-50" },
    { title: "Total Recharge", value: `$${(kpis.totalRecharge || 0).toLocaleString()}`, change: "+14.2%", isPositive: true, icon: DollarSign, color: "text-indigo-600", bg: "bg-indigo-50" },
    { title: "Circulating Coins", value: (kpis.totalCoins || 0).toLocaleString(), change: "+9.5%", isPositive: true, icon: Coins, color: "text-yellow-600", bg: "bg-yellow-50" },
    { title: "Estimated Profit", value: `$${(kpis.profit || 0).toLocaleString()}`, change: "+15.0%", isPositive: true, icon: TrendingUp, color: "text-pink-600", bg: "bg-pink-50" },
  ];

  return (
    <div className="max-w-[1600px] mx-auto space-y-8 animate-in fade-in duration-300">
      
      {/* 1. Title Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-100 pb-5">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-800 tracking-tight">Main Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Control your live streaming app circulation, recharge flow, and security logs.</p>
        </div>
        <button 
          onClick={fetchKPIs} 
          className="p-2.5 bg-white border border-slate-200 text-slate-700 font-semibold rounded-xl hover:bg-slate-50 active:scale-95 transition shadow-sm flex items-center justify-center"
          title="Refresh Data"
        >
          <RefreshCw size={20} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* 2. Quick Actions Panel */}
      <div className="space-y-4">
        <h2 className="text-xl font-black text-slate-800 tracking-tight flex items-center gap-2">
          <span className="w-2.5 h-6 bg-pink-600 rounded-full"></span>
          Quick Administrative Controls
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Card 1: Add Seller */}
          <div 
            onClick={() => { setModalType('seller'); setModalError(''); setModalSuccess(''); }}
            className="group relative bg-white rounded-3xl p-6 border border-slate-100 hover:border-pink-500/20 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden flex flex-col justify-between h-44"
          >
            <div className="absolute -right-8 -bottom-8 w-24 h-24 bg-pink-500/5 rounded-full blur-xl group-hover:scale-150 transition-all duration-500"></div>
            
            <div className="space-y-3">
              <div className="inline-flex p-3 bg-pink-50 text-pink-600 rounded-xl group-hover:bg-pink-600 group-hover:text-white transition-all duration-300">
                <PlusCircle size={22} className="group-hover:rotate-90 transition-all duration-300" />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 group-hover:text-pink-600 transition-colors">Add Coin Seller</h3>
                <p className="text-slate-400 text-xs mt-1">Register new authorized coin sellers into the system.</p>
              </div>
            </div>
            
            <div className="flex items-center gap-1 text-pink-600 font-bold text-xs group-hover:translate-x-1.5 transition-transform mt-4">
              Access Tool <ArrowRight size={14} />
            </div>
          </div>

          {/* Card 2: Block Actor */}
          <div 
            onClick={() => { setModalType('block'); setModalError(''); setModalSuccess(''); setBlockUserSearch(''); setBlockUsersList([]); }}
            className="group relative bg-white rounded-3xl p-6 border border-slate-100 hover:border-indigo-500/20 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden flex flex-col justify-between h-44"
          >
            <div className="absolute -right-8 -bottom-8 w-24 h-24 bg-indigo-500/5 rounded-full blur-xl group-hover:scale-150 transition-all duration-500"></div>

            <div className="space-y-3">
              <div className="inline-flex p-3 bg-indigo-50 text-indigo-600 rounded-xl group-hover:bg-indigo-600 group-hover:text-white transition-all duration-300">
                <UserMinus size={22} />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 group-hover:text-indigo-600 transition-colors">Block Actor</h3>
                <p className="text-slate-400 text-xs mt-1">Instantly ban suspicious accounts and flag for review.</p>
              </div>
            </div>

            <div className="flex items-center gap-1 text-indigo-600 font-bold text-xs group-hover:translate-x-1.5 transition-transform mt-4">
              Access Tool <ArrowRight size={14} />
            </div>
          </div>

          {/* Card 3: Manual Recharge */}
          <div 
            onClick={() => { setModalType('recharge'); setModalError(''); setModalSuccess(''); setUserSearch(''); setUsersList([]); }}
            className="group relative bg-white rounded-3xl p-6 border border-slate-100 hover:border-purple-500/20 shadow-sm hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden flex flex-col justify-between h-44"
          >
            <div className="absolute -right-8 -bottom-8 w-24 h-24 bg-purple-500/5 rounded-full blur-xl group-hover:scale-150 transition-all duration-500"></div>

            <div className="space-y-3">
              <div className="inline-flex p-3 bg-purple-50 text-purple-600 rounded-xl group-hover:bg-purple-600 group-hover:text-white transition-all duration-300">
                <CreditCard size={22} />
              </div>
              <div>
                <h3 className="font-bold text-slate-800 group-hover:text-purple-600 transition-colors">Manual Recharge</h3>
                <p className="text-slate-400 text-xs mt-1">Directly credit coins into user wallets with duplicate checks.</p>
              </div>
            </div>

            <div className="flex items-center gap-1 text-purple-600 font-bold text-xs group-hover:translate-x-1.5 transition-transform mt-4">
              Access Tool <ArrowRight size={14} />
            </div>
          </div>
        </div>
      </div>

      {/* 3. Stats KPIs Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-2xl border border-slate-100 h-28 animate-pulse"></div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {statsData.map((item, index) => (
            <div key={index} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition duration-200">
              <div className="flex justify-between items-start">
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">{item.title}</p>
                <div className={`p-2 rounded-lg ${item.bg} ${item.color}`}>
                  <item.icon size={18} />
                </div>
              </div>
              <h3 className="text-2xl font-black text-slate-800 tracking-tight mt-3">{item.value}</h3>
              <p className="text-[10px] font-bold text-slate-400 mt-2 flex items-center gap-1">
                <span className="text-green-500">★</span> {item.change}
              </p>
            </div>
          ))}
        </div>
      )}

      {/* 4. Live Activity & Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Graph */}
        <div className="lg:col-span-2 bg-white p-6 rounded-3xl border border-slate-100 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-extrabold text-slate-800 text-lg">Live Online Activity</h3>
            <span className="px-3 py-1 bg-green-50 text-green-600 rounded-full text-xs font-bold animate-pulse">● Live Tracking</span>
          </div>
          <div className="h-[300px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={kpis.liveActivity} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#db2777" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#db2777" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F1F5F9" />
                <XAxis dataKey="time" axisLine={false} tickLine={false} tick={{fill: '#64748B', fontSize: 11}} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748B', fontSize: 11}} />
                <Tooltip 
                  contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)' }}
                />
                <Area type="monotone" dataKey="count" stroke="#db2777" strokeWidth={3} fillOpacity={1} fill="url(#colorCount)" name="Online Users" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Live Logs Feed */}
        <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm flex flex-col">
          <h3 className="font-extrabold text-slate-800 text-lg mb-4">System Monitor Log</h3>
          <div className="flex-1 overflow-y-auto space-y-4 pr-1 max-h-[300px]">
            <div className="flex gap-3 text-sm p-3 bg-slate-50 rounded-xl">
              <div className="w-2 h-2 rounded-full bg-green-500 mt-1.5 shrink-0 animate-ping"></div>
              <div>
                <p className="font-semibold text-slate-700">Online Monitor Active</p>
                <p className="text-xs text-slate-400 mt-0.5">Socket connection list synchronized.</p>
              </div>
            </div>
            <div className="flex gap-3 text-sm p-3 bg-slate-50 rounded-xl">
              <div className="w-2 h-2 rounded-full bg-blue-500 mt-1.5 shrink-0"></div>
              <div>
                <p className="font-semibold text-slate-700">Seller System Stable</p>
                <p className="text-xs text-slate-400 mt-0.5">Automatic bonus and event commission levels checked.</p>
              </div>
            </div>
            <div className="flex gap-3 text-sm p-3 bg-slate-50 rounded-xl">
              <div className="w-2 h-2 rounded-full bg-yellow-500 mt-1.5 shrink-0"></div>
              <div>
                <p className="font-semibold text-slate-700">Fraud Engine Monitoring</p>
                <p className="text-xs text-slate-400 mt-0.5">Alert rules: duplicate transactions & IP tracking active.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================== */}
      {/* 5. MODAL POPUPS */}
      {/* ========================================================== */}
      {modalType && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in duration-200">
            {/* Header */}
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-white">
              <h3 className="text-xl font-bold text-slate-800">
                {modalType === 'seller' && "Add New Coin Seller"}
                {modalType === 'block' && "Block / Unblock User"}
                {modalType === 'recharge' && "Execute Manual Coins Recharge"}
              </h3>
              <button onClick={() => setModalType(null)} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50 transition">
                <X size={24} />
              </button>
            </div>

            {/* Error / Success */}
            <div className="px-6 mt-4">
              {modalError && (
                <div className="p-4 bg-red-50 text-red-600 border border-red-100 rounded-xl text-sm font-semibold flex items-center gap-2">
                  <AlertTriangle size={18} /> {modalError}
                </div>
              )}
              {modalSuccess && (
                <div className="p-4 bg-green-50 text-green-600 border border-green-100 rounded-xl text-sm font-semibold flex items-center gap-2">
                  <CheckCircle2 size={18} /> {modalSuccess}
                </div>
              )}
            </div>

            {/* Form */}
            {modalType === 'seller' && (
              <form onSubmit={handleAddSeller} className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <input
                    required
                    placeholder="First Name"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                    value={sellerForm.first_name}
                    onChange={(e) => setSellerForm({ ...sellerForm, first_name: e.target.value })}
                  />
                  <input
                    required
                    placeholder="Last Name"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                    value={sellerForm.last_name}
                    onChange={(e) => setSellerForm({ ...sellerForm, last_name: e.target.value })}
                  />
                </div>
                <input
                  required
                  type="email"
                  placeholder="Seller Email Address"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                  value={sellerForm.email}
                  onChange={(e) => setSellerForm({ ...sellerForm, email: e.target.value })}
                />
                <div className="relative w-full">
                  <input
                    required
                    type={showPassword ? "text" : "password"}
                    placeholder="Seller Access Password"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition pr-10"
                    value={sellerForm.password}
                    onChange={(e) => setSellerForm({ ...sellerForm, password: e.target.value })}
                  />
                  <button 
                    type="button" 
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-pink-600 transition"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div className="px-1">
                  <p className="text-xs font-semibold text-slate-500">
                    Strength Requirements: 
                    <span className={sellerForm.password.length >= 6 ? "text-green-500 ml-1" : "text-slate-400 ml-1"}>Min 6 chars</span>
                  </p>
                </div>
                <select
                  required
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                  value={sellerForm.country}
                  onChange={(e) => setSellerForm({ ...sellerForm, country: e.target.value })}
                >
                  <option value="" disabled>Select Country</option>
                  {countriesLoading ? (
                    <option disabled>Loading countries...</option>
                  ) : (
                    countriesList.map(country => (
                      <option key={country} value={country}>{country}</option>
                    ))
                  )}
                </select>
                <div className="mt-4">
                  <input
                    required
                    placeholder="WhatsApp Number (Mandatory)"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                    value={sellerForm.whatsapp_number}
                    onChange={(e) => setSellerForm({ ...sellerForm, whatsapp_number: e.target.value })}
                  />
                  <p className="text-[10px] text-slate-400 mt-1 pl-1">Required for 2FA and transactional alerts.</p>
                </div>
                <div className="mt-4">
                  <input
                    placeholder={sellerForm.country === 'India' ? "Aadhar Card (Optional)" : "National ID / Passport (Optional)"}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition"
                    value={sellerForm.aadhar_card}
                    onChange={(e) => setSellerForm({ ...sellerForm, aadhar_card: e.target.value })}
                  />
                  <p className="text-[10px] text-slate-400 mt-1 pl-1">Helps in faster KYC and verified badge allocation.</p>
                </div>

                <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
                  <button type="button" onClick={() => setModalType(null)} className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-100 rounded-xl">Cancel</button>
                  <button type="submit" disabled={modalLoading} className="px-6 py-2.5 bg-pink-600 text-white rounded-xl font-bold shadow-lg shadow-pink-200">
                    {modalLoading ? "Creating..." : "Apply & Add Seller"}
                  </button>
                </div>
              </form>
            )}

            {modalType === 'block' && (
              <form onSubmit={handleBlockUser} className="p-6 space-y-4">
                <div className="relative">
                  <label className="text-xs font-bold text-slate-400 uppercase">Search User to Block/Unblock</label>
                  <input
                    placeholder="Search by Email or User ID Code"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition mt-1"
                    value={blockUserSearch}
                    onChange={(e) => handleSearchBlockUsers(e.target.value)}
                  />
                  {blockUsersList.length > 0 && (
                    <div className="absolute top-16 left-0 w-full bg-white border border-slate-200 rounded-xl shadow-lg z-20 max-h-48 overflow-y-auto">
                      {blockUsersList.map((u) => (
                        <div 
                          key={u.id} 
                          onClick={() => {
                            setBlockForm({ ...blockForm, user_id: u.id });
                            setBlockUserSearch(`${u.first_name} ${u.last_name} (${u.email})`);
                            setBlockUsersList([]);
                          }}
                          className="p-3 hover:bg-slate-50 cursor-pointer text-sm font-semibold border-b border-slate-100"
                        >
                          {u.first_name} {u.last_name} - {u.email}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="ban"
                      checked={blockForm.ban === true}
                      onChange={() => setBlockForm({ ...blockForm, ban: true })}
                    />
                    <span className="text-sm font-bold text-slate-700">Ban / Freeze User</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="ban"
                      checked={blockForm.ban === false}
                      onChange={() => setBlockForm({ ...blockForm, ban: false })}
                    />
                    <span className="text-sm font-bold text-slate-700">Unban / Activate User</span>
                  </label>
                </div>

                {blockForm.ban && (
                  <div>
                    <label className="text-xs font-bold text-slate-400 uppercase">Ban Reason (Will log a security warning)</label>
                    <textarea
                      placeholder="Specify ban reason..."
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition mt-1 h-24"
                      value={blockForm.reason}
                      onChange={(e) => setBlockForm({ ...blockForm, reason: e.target.value })}
                    />
                  </div>
                )}

                <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
                  <button type="button" onClick={() => setModalType(null)} className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-100 rounded-xl">Cancel</button>
                  <button type="submit" disabled={modalLoading} className="px-6 py-2.5 bg-slate-900 text-white rounded-xl font-bold">
                    {modalLoading ? "Processing..." : "Update Status"}
                  </button>
                </div>
              </form>
            )}

            {modalType === 'recharge' && (
              <form onSubmit={handleManualRecharge} className="p-6 space-y-4">
                <div className="relative">
                  <label className="text-xs font-bold text-slate-400 uppercase">Search User</label>
                  <input
                    placeholder="Search by Email or User ID Code"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition mt-1"
                    value={userSearch}
                    onChange={(e) => handleSearchUsers(e.target.value)}
                  />
                  {usersList.length > 0 && (
                    <div className="absolute top-16 left-0 w-full bg-white border border-slate-200 rounded-xl shadow-lg z-20 max-h-48 overflow-y-auto">
                      {usersList.map((u) => (
                        <div 
                          key={u.id} 
                          onClick={() => {
                            setRechargeForm({ ...rechargeForm, target_user_id: u.id });
                            setUserSearch(`${u.first_name} ${u.last_name} (${u.email})`);
                            setUsersList([]);
                          }}
                          className="p-3 hover:bg-slate-50 cursor-pointer text-sm font-semibold border-b border-slate-100"
                        >
                          {u.first_name} {u.last_name} - {u.email}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-slate-400 uppercase flex justify-between">
                      <span>Amount Paid</span>
                      {rechargeForm.amount && (
                        <span className="text-pink-600 font-bold">
                          ₹{formatNumberWithCommas(rechargeForm.amount)}
                        </span>
                      )}
                    </label>
                    <input
                      required
                      type="text"
                      placeholder="e.g. 500"
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition mt-1 font-bold"
                      value={rechargeForm.amount}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/,/g, '').replace(/[^0-9.]/g, '');
                        setRechargeForm({ ...rechargeForm, amount: raw ? Number(raw).toLocaleString('en-IN') : '' });
                      }}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-400 uppercase flex justify-between items-center">
                      <span>Coins Credited</span>
                      {getRawNumber(rechargeForm.coins) > 0 && (
                        <span className="text-[10px] font-black text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                          {formatAmountInWords(getRawNumber(rechargeForm.coins))}
                        </span>
                      )}
                    </label>
                    <div className="relative mt-1">
                      <input
                        required
                        type="text"
                        placeholder="e.g. 20,000"
                        className="w-full pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition font-black text-slate-800"
                        value={rechargeForm.coins}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
                          setRechargeForm({ ...rechargeForm, coins: raw ? Number(raw).toLocaleString('en-IN') : '' });
                        }}
                      />
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs pointer-events-none">
                        🪙
                      </div>
                    </div>
                  </div>
                </div>

                {/* Quick preset chips for coins */}
                <div className="flex flex-wrap gap-1.5 pt-0.5">
                  {[1000, 5000, 10000, 20000, 50000, 100000, 500000, 1000000].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setRechargeForm({ ...rechargeForm, coins: preset.toLocaleString('en-IN') })}
                      className="px-2 py-0.5 bg-slate-100 hover:bg-pink-50 hover:text-pink-600 hover:border-pink-200 border border-slate-200 rounded-lg text-[10px] font-bold text-slate-600 transition active:scale-95"
                    >
                      +{preset.toLocaleString('en-IN')}
                    </button>
                  ))}
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-400 uppercase">Wrong ID Approved Correction (Optional)</label>
                  <input
                    placeholder="Specify wrong ID entered by user, if correcting"
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-pink-500/20 transition mt-1"
                    value={rechargeForm.wrong_id}
                    onChange={(e) => setRechargeForm({ ...rechargeForm, wrong_id: e.target.value })}
                  />
                  <p className="text-[10px] text-slate-400 mt-1">If entered, this recharge will be queued in the Recharge queue for wrong ID correction approval.</p>
                </div>

                <div className="pt-4 border-t border-slate-100 flex justify-end gap-3">
                  <button type="button" onClick={() => setModalType(null)} className="px-6 py-2.5 text-slate-500 font-bold hover:bg-slate-100 rounded-xl">Cancel</button>
                  <button type="submit" disabled={modalLoading} className="px-6 py-2.5 bg-pink-600 text-white rounded-xl font-bold shadow-lg shadow-pink-200">
                    {modalLoading ? "Processing..." : "Submit Recharge"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;